import { apiClient } from './api';
import { API_ENDPOINTS, APIHeadersWithAuth } from '../globalConstants';
import type { Service, PrintMaterial } from '@/lib/types';

export interface ServiceInput {
    name: string;
    category?: string;
    kind?: string;
    sellingPrice?: number;
    dealerPrice?: number;
    isCommissioned?: boolean;
    defaultCommission?: number;
    modes?: any[];
    rates?: any[];
    bom?: any[];
    laborCharge?: number;
    fulfillment?: string;
    team?: string | null;
    outsourced?: boolean;
    vendorId?: string | null;
    outsideCost?: number;
    description?: string;
}

export interface PrintMaterialInput {
    name: string;
    unit?: string;
    costPerUnit?: number;
    sellingPrice?: number;
    stockQuantity?: number;
    lowStockThreshold?: number;
    category?: string;
}

export async function listServices(shopId: number | string): Promise<Service[]> {
    const res = await apiClient.get<{ data: any[] }>(
        API_ENDPOINTS.services.LIST(shopId),
        { headers: APIHeadersWithAuth() }
    );
    return res.data;
}

export async function createService(
    shopId: number | string,
    input: ServiceInput
): Promise<Service> {
    const res = await apiClient.post<{ data: any }>(
        API_ENDPOINTS.services.CREATE(shopId),
        input,
        { headers: APIHeadersWithAuth() }
    );
    return res.data;
}

export async function updateService(
    shopId: number | string,
    id: number | string,
    input: Partial<ServiceInput>
): Promise<Service> {
    const res = await apiClient.put<{ data: any }>(
        API_ENDPOINTS.services.UPDATE(shopId, id),
        input,
        { headers: APIHeadersWithAuth() }
    );
    return res.data;
}

export async function deleteService(
    shopId: number | string,
    id: number | string
): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.services.DELETE(shopId, id), {
        headers: APIHeadersWithAuth(),
    });
}

export async function listPrintMaterials(
    shopId: number | string
): Promise<PrintMaterial[]> {
    const res = await apiClient.get<{ data: any[] }>(
        API_ENDPOINTS.printMaterials.LIST(shopId),
        { headers: APIHeadersWithAuth() }
    );
    return res.data;
}

export async function createPrintMaterial(
    shopId: number | string,
    input: PrintMaterialInput
): Promise<PrintMaterial> {
    const res = await apiClient.post<{ data: any }>(
        API_ENDPOINTS.printMaterials.CREATE(shopId),
        input,
        { headers: APIHeadersWithAuth() }
    );
    return res.data;
}

export async function updatePrintMaterial(
    shopId: number | string,
    id: number | string,
    input: Partial<PrintMaterialInput>
): Promise<PrintMaterial> {
    const res = await apiClient.put<{ data: any }>(
        API_ENDPOINTS.printMaterials.UPDATE(shopId, id),
        input,
        { headers: APIHeadersWithAuth() }
    );
    return res.data;
}

export async function deletePrintMaterial(
    shopId: number | string,
    id: number | string
): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.printMaterials.DELETE(shopId, id), {
        headers: APIHeadersWithAuth(),
    });
}

export async function adjustMaterialStock(
    shopId: number | string,
    id: number | string,
    input: { change: number; reason: string }
): Promise<PrintMaterial> {
    const res = await apiClient.post<{ data: any }>(
        API_ENDPOINTS.printMaterials.STOCK(shopId, id),
        input,
        { headers: APIHeadersWithAuth() }
    );
    return res.data;
}

export async function recordMaterialWastage(
    shopId: number | string,
    id: number | string,
    input: { qty: number; note?: string }
): Promise<PrintMaterial> {
    const res = await apiClient.post<{ data: any }>(
        API_ENDPOINTS.printMaterials.WASTAGE(shopId, id),
        input,
        { headers: APIHeadersWithAuth() }
    );
    return res.data;
}
